import { Box, Typography, useTheme } from "@mui/material";
import type { ReactNode } from "react";
import { Wordmark } from "@/brand/Logo";

/**
 * The signed-out frame (MultiDoc design system sign-in, as in ecm-frontend's
 * LoginBrandPanel): the brand colour, the MultiDoc mark and one line about the
 * product over quiet document line-art on the left; the form on the right. No
 * gradient and no feature bullet list.
 *
 * The panel collapses away below `md` rather than stacking, so a phone gets the
 * form without scrolling past it.
 */
export default function AuthLayout({ children }: { children: ReactNode }) {
  const theme = useTheme();
  const ink = theme.palette.header.contrastText;
  return (
    <Box sx={{ minHeight: "100vh", display: "flex", bgcolor: "background.default" }}>
      <Box
        component="aside"
        sx={{
          position: "relative",
          overflow: "hidden",
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "space-between",
          flex: 1,
          px: { md: 5, lg: 8 },
          py: 6,
          bgcolor: "header.main",
          color: "header.contrastText",
        }}
      >
        <Box sx={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 1.25 }}>
          <Wordmark onBrand height={30} />
          <Typography component="span" sx={{ fontSize: "1.1111rem", color: "inherit" }}>
            Console
          </Typography>
        </Box>

        <Box sx={{ position: "relative", zIndex: 1, maxWidth: 520 }}>
          <Typography component="p" sx={{ fontSize: { md: "2.2222rem", lg: "2.7778rem" }, fontWeight: 600, lineHeight: 1.2, color: "inherit" }}>
            One place to run MultiDoc.
          </Typography>
          <Typography sx={{ mt: 2, fontSize: "1.1111rem", lineHeight: 1.55, color: "inherit", opacity: 0.85 }}>
            What customers are licensed for, what the fleet is running, and what gets deployed next.
          </Typography>
        </Box>

        <Typography variant="caption" sx={{ position: "relative", zIndex: 1, color: "inherit", opacity: 0.85 }}>
          © MultiDoc. Vendor and partner access only.
        </Typography>

        {/* Line-art: stacked documents, a folder tab and an approval tick. */}
        <Box
          component="svg"
          viewBox="0 0 320 320"
          aria-hidden
          sx={{
            position: "absolute",
            right: { md: -140, lg: -90 },
            bottom: { md: 90, lg: 70 },
            width: { md: 340, lg: 400 },
            color: ink,
            opacity: 0.12,
            pointerEvents: "none",
          }}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="70" y="70" width="170" height="220" rx="14" />
          <rect x="95" y="40" width="170" height="220" rx="14" />
          <path d="M120 16h70l14 14h86a14 14 0 0 1 14 14v176" />
          <path d="M125 90h110M125 115h110M125 140h80M125 175h110M125 200h60" />
          <circle cx="232" cy="222" r="26" />
          <path d="m220 222 8 8 16-17" />
        </Box>
      </Box>

      <Box sx={{ flex: { xs: 1, md: "0 0 min(560px, 50%)" }, display: "grid", placeItems: "center", p: { xs: 2, sm: 4 } }}>
        <Box sx={{ width: "100%", maxWidth: 380 }}>
          {/* The mark again, small, for the narrow layout that has no panel. */}
          <Box sx={{ display: { xs: "block", md: "none" }, mb: 3 }}>
            <Wordmark height={26} />
          </Box>
          {children}
        </Box>
      </Box>
    </Box>
  );
}

